import React from "react"
import { Building2, ShieldCheck, MapPin, Globe, Calendar, TrendingUp } from "lucide-react"
import { cn } from "@/lib/utils"

// ── Radial Match Score ────────────────────────────────────────────────────────
function MatchScore({ pct }: { pct: number }) {
  const deg = Math.round((pct / 100) * 360)
  return (
    <div
      className="relative size-[36px] shrink-0 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform duration-300"
      style={{
        background: `conic-gradient(#e6202f 0deg ${deg}deg, #fcdbd8 ${deg}deg 360deg)`,
      }}
      title={`${pct}% AI Match`}
    >
      <div className="absolute size-[28px] rounded-full bg-white flex items-center justify-center shadow-inner">
        <span className="text-[9px] font-bold text-on-surface">{pct}%</span>
      </div>
    </div>
  )
}

export interface SupplierCardProps {
  supplier?: any
  isMultiSelectActive?: boolean
  isSelected?: boolean
  isActiveTarget?: boolean
  onSelectToggle?: () => void
  onViewDetails?: () => void
  isLoading?: boolean
}

export function SupplierCard({
  supplier,
  isMultiSelectActive = false,
  isSelected = false,
  isActiveTarget = false,
  onSelectToggle = () => {},
  onViewDetails = () => {},
  isLoading = false,
}: SupplierCardProps) {
  if (isLoading) {
    return (
      <div className="relative overflow-hidden flex flex-col justify-between h-full rounded-xl p-4.5 bg-white border border-slate-200 animate-pulse text-left">
        <div className="flex flex-col gap-3.5 flex-1 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3 items-start min-w-0 flex-1">
              <div className="w-12 h-12 bg-slate-200 rounded-lg shrink-0" />
              <div className="min-w-0 flex-1 space-y-2 mt-1">
                <div className="h-4 bg-slate-200 rounded-sm w-3/4" />
                <div className="h-3 bg-slate-200 rounded-sm w-1/2" />
              </div>
            </div>
          </div>
          <div className="h-3 bg-slate-200 rounded-sm w-full mt-2" />
          <div className="h-3 bg-slate-200 rounded-sm w-5/6" />
          <div className="flex items-center gap-1.5 mt-2">
            <div className="size-3.5 bg-slate-200 rounded-full shrink-0" />
            <div className="h-3 bg-slate-200 rounded-sm w-1/3" />
          </div>
          <div className="h-10 bg-slate-100 rounded-lg w-full mt-auto" />
        </div>
        <div className="h-9.5 bg-slate-200 rounded-lg w-full mt-3" />
      </div>
    )
  }

  const matchPercentage = supplier.rank !== undefined ? Math.round(supplier.rank * 100) : null
  const hasInfoGridFields = Boolean(
    (supplier.type === "external" && supplier.rating) ||
      supplier.yearEstablished ||
      supplier.annualRevenue
  )

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest("a") || target.closest("button") || target.closest("input[type='checkbox']")) return
    if (isMultiSelectActive) {
      onSelectToggle()
    } else {
      onViewDetails()
    }
  }

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "group relative overflow-hidden flex flex-col justify-between h-full rounded-xl p-4.5 transition-all duration-300 text-left font-sans",
        isActiveTarget || isSelected
          ? "bg-white border border-[#be001e] shadow-sm"
          : "bg-white border border-slate-200 hover:border-slate-300 hover:shadow-[0_12px_30px_rgba(230,32,47,0.05)] hover:-translate-y-1"
      )}
    >
      {/* Top Accent Glow */}
      <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-xl opacity-0 group-hover:opacity-100 transition-all duration-300" />

      {/* Main Content Area */}
      <div className="flex flex-col gap-3.5 flex-1 pb-4 ">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3 items-start min-w-0">
            {isMultiSelectActive && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={onSelectToggle}
                className="mt-1.5 size-3.5 accent-[#be001e] cursor-pointer shrink-0"
              />
            )}
            {/* Logo box */}
            <div className="w-12 h-12 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg flex items-center justify-center p-1.5 shadow-sm shrink-0 group-hover:scale-105 group-hover:border-[#be001e]/30 group-hover:shadow-md transition-all duration-300">
              <Building2 className="size-5 text-secondary group-hover:text-[#be001e] transition-colors duration-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-1.5 mb-1">
                <h3 className="text-[15px] font-semibold leading-5 text-on-surface group-hover:text-[#be001e] transition-colors duration-300 break-words">{supplier.name}</h3>
                {supplier.type === "internal" && supplier.verificationStatus === "APPROVED" && (
                  <ShieldCheck className="size-[15px] text-blue-500 shrink-0 mt-0.5" />
                )}
              </div>
            </div>
          </div>

          {/* Radial score */}
          {matchPercentage !== null && <MatchScore pct={matchPercentage} />}
        </div>

        {supplier.companyType && (
          <p className="text-[12px] leading-[18px] text-slate-500 line-clamp-2">
            <span className="font-semibold text-slate-700">Type:</span> {supplier.companyType}
          </p>
        )}

        {/* Description */}
        {supplier.description && (
          <p className="text-[12px] leading-[18px] text-slate-500 line-clamp-2">{supplier.description}</p>
        )}

        {/* Location */}
        {(supplier.location || supplier.city || supplier.state || supplier.country) && (
          <div className="flex items-center gap-1.5 text-[12px] text-slate-600 font-medium">
            <MapPin className="size-3.5 text-[#be001e] shrink-0" />
            <span className="truncate">
              {supplier.location || [supplier.city, supplier.state, supplier.country].filter(Boolean).join(", ")}
            </span>
          </div>
        )}

        {/* Info grid — only renders fields that exist */}
        {hasInfoGridFields && (
          <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-y-1.5 gap-x-3 p-3 bg-slate-50/70 border border-slate-100 rounded-lg text-[11.5px] text-on-surface/70 group-hover:bg-slate-50/90 transition-colors duration-300 mt-auto">

            {/* Rating (external) */}
            {supplier.type === "external" && supplier.rating ? (
              <div className="flex items-center gap-1 text-slate-600 min-w-0">
                <span className="text-amber-500 font-bold text-[11px] shrink-0">★ {supplier.rating}</span>
                {supplier.reviewsCount && (
                  <span className="text-slate-400 text-[10px] truncate">({supplier.reviewsCount})</span>
                )}
              </div>
            ) : null}

            {/* Year Established */}
            {supplier.yearEstablished ? (
              <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                <Calendar className="size-3.5 text-slate-400 shrink-0" />
                <span className="truncate">Est. {supplier.yearEstablished}</span>
              </div>
            ) : null}

            {/* Annual Revenue */}
            {supplier.annualRevenue ? (
              <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                <TrendingUp className="size-3.5 text-slate-400 shrink-0" />
                <span className="truncate">{supplier.annualRevenue}</span>
              </div>
            ) : null}

          </div>
        )}

      </div>

      {/* Actions */}
      {!isMultiSelectActive && (
        <div className="flex gap-2 mt-3">
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetails() }}
            className="group/btn flex-1 h-9.5 bg-gradient-to-r from-[#be001e] to-[#e60024] hover:brightness-110 text-white rounded-lg flex items-center justify-center gap-2 text-[12px] font-semibold shadow-md shadow-[#be001e]/20 hover:shadow-lg hover:shadow-[#be001e]/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-200 cursor-pointer"
          >
            Details
          </button>
        </div>
      )}
    </div>
  )
}